<!-- 聊天主体： 头像+对话框 -->
<template>
  <div class="chat-content">
    <TimeStamp v-if="showTime && text.trim() !== ''" :timestamp="timestamp" />
    <!-- 评分卡 -->
    <div v-if="sender === SenderType.SYS && type === MessageType.GC" class="comment-wrapper">
      <CommentCard :content="ratingContent" />
    </div>
    <!-- 系统消息 -->
    <div v-else-if="sender === SenderType.SYS && text.trim() === ''"></div>
    <div v-else-if="sender === SenderType.SYS" class="system-message">
      <span class="w-400 s-12 l-20 text-2">{{
        queue !== undefined ? text.replace('{num}', queue.toString()) : text
      }}</span>
    </div>
    <!-- 气泡对话框 -->
    <div v-else class="main-chat" :class="{
      'my-chat': isMe,
      'first-chat': isFirst,
    }">
      <!-- 头像 -->
      <component
        v-if="(isFirst || type === MessageType.RECOM) && !isMe && cStore.agentAvatar !== '' && username !== 'System'"
        :is="getAvatar(avatar)" class="message-avatar" :style="{
          width: '36px',
          height: '36px',
          'flex-grow': 0,
          'flex-shrink': 0,
          borderRadius: '50%',
          overflow: 'hidden',
        }" />
      <ChannelAvatar v-else-if="(isFirst || type === MessageType.RECOM) && !isMe" class="message-avatar" :id="avatar"
        :name="username" size="36px" />

      <!-- 对话框 -->
      <div class="main-chat-wrapper">
        <span v-if="isFirst && !isMe" class="w-500 s-12 l-20 username-label">{{ username }}</span>
        <div class="tip-wrapper">
          <Tooltip :content="$t('session.warn')">
            <WarnIcon v-if="isWarn && isMe" />
          </Tooltip>

          <Tooltip :content="$t('session.block')" :style="{ 'margin-right': '8px' }">
            <BlockIcon v-if="isBlocked && isMe" />
          </Tooltip>

          <div class="main-chat-bubble" :class="{
            'my-chat': isMe,
            'first-chat': isFirst,
            'is-agent': isAgent,
            'plain-bubble': isPlainTextMessage,
            'is-recom': type === MessageType.RECOM,
            'is-mq': type === MessageType.MQ,
          }" :style="{
            '--bubble-color': bubbleColor,
            '--legacy-bubble-color': legacyBubbleColor,
          }">
            <!-- File -->
            <FileCard v-if="fileSize !== undefined && fileSize !== null && fileSize !== '0'" :text="text"
              :fileSize="fileSize" :duration="duration" :file-name="fileName" />
            <!-- image -->
            <img v-else-if="type && type.includes('image')" :src="text" :style="{ maxWidth: '200px' }"
              @click="showImage = true" />
            <!-- video（动图） -->
            <video v-else-if="type && type.includes('video')" autoplay loop muted playsinline
              :style="{ maxWidth: '200px' }">
              <source :src="text" :type="type" />
            </video>
            <!-- 邮件消息提醒 -->
            <div v-else-if="type && type.includes('email')">
              <span>{{ $t('session.email.placeholder') + JSON.parse(text).to[0].address }}</span>
            </div>
            <!-- 问题卡片 -->
            <RecommendationCard v-else-if="type === MessageType.RECOM" :content="JSON.parse(text)" />
            <!-- 多答案候选（matchQuestions） -->
            <MatchQuestionsCard v-else-if="type === MessageType.MQ" :items="matchQuestionItems" :timestamp="timestamp"
              :message-id="messageId" />
            <!-- 纯文字 -->
            <div v-else class="plain-text">
              <span class="w-400 s-14 l-20 main-chat-bubble-text" :style="{ 'white-space': 'pre-line' }">{{ queue !==
                undefined ? text.replace('{num}', queue.toString()) : text }}</span>
              <span class="main-chat-bubble-time" :class="timeColorClass">{{ shortTime }}</span>
            </div>
          </div>
          <!-- matchQuestions 更多答案数量徽标：置于气泡外部，点击后打开完整答案弹窗 -->
          <button v-if="type === MessageType.MQ && matchQuestionItems.length > 1" type="button" class="mq-more-outside"
            @click.stop="handleOpenMatchQuestions">{{ matchQuestionItems.length }}</button>
        </div>
      </div>

      <Transition>
        <div v-if="type === 'image' && showImage" class="full-screen-image" @click="showImage = false">
          <img :src="text" :style="{
            maxWidth: `calc(100vw / ${zoomRatio})`,
            maxHeight: `calc(100vh / ${zoomRatio})`,
          }" />
        </div>
      </Transition>
    </div>
  </div>
</template>

<script lang="ts">
/**
 * Purpose: Render one normalized chat message while keeping text bubbles visually distinct from
 * special message cards.
 * Props: isFirst controls the 36px avatar track and sender label; bubbleColor is the normalized
 * Widget brand color used only by visitor text bubbles.
 */
import { computed, defineComponent, inject, PropType, ref } from 'vue'
import TimeStamp from './TimeStamp.vue'
import ChannelAvatar from '../../avatar/ChannelAvatar.vue'
import { useChatStore } from '../../../store/chat'
import { resolveAvatar } from '../../avatar/resolveAvatar'
import FileCard from './FileCard.vue'
import { MessageType, SenderType } from '../../../enum/chatEnum'
import CommentCard from './CommentCard.vue'
import RecommendationCard from './RecommendationCard.vue'
import MatchQuestionsCard from './MatchQuestionsCard.vue'
import { parseMatchQuestions, sortMatchQuestions, type MatchQuestionItem, type OpenMatchQuestionsFn } from './matchQuestionsTypes'
import WarnIcon from '../../icon/WarnIcon.vue'
import BlockIcon from '../../icon/BlockIcon.vue'
import Tooltip from './Tooltip.vue'

type RatingScore = '1' | '2' | '3' | '4' | '5'
type RatingContent = Record<RatingScore, string>

const EMPTY_RATING_CONTENT: RatingContent = {
  '1': '',
  '2': '',
  '3': '',
  '4': '',
  '5': '',
}

const RATING_SCORES: RatingScore[] = ['1', '2', '3', '4', '5']

export default defineComponent({
  name: 'CustomerContent',
  components: {
    ChannelAvatar,
    TimeStamp,
    FileCard,
    CommentCard,
    RecommendationCard,
    MatchQuestionsCard,
    WarnIcon,
    BlockIcon,
    Tooltip,
  },
  props: {
    username: {
      type: String,
      default: 'username',
    },
    avatar: {
      type: String,
      default: '0',
    },
    text: {
      type: String,
      default:
        'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore',
    },
    sender: {
      type: String as PropType<string | undefined>,
      default: SenderType.SYS,
    },
    queue: {
      type: Object as PropType<number | undefined>,
      default: undefined,
    },
    timestamp: {
      type: String,
      default: '2025-01-01 00:00:00',
    },
    type: {
      type: String,
      default: 'text/plain',
    },
    isMe: {
      type: Boolean,
      default: false,
    },
    isFirst: {
      type: Boolean,
      default: true,
    },
    isAgent: {
      type: Boolean,
      default: false,
    },
    isWarn: {
      type: Boolean,
      default: false,
    },
    isBlocked: {
      type: Boolean,
      default: false,
    },
    showTime: {
      type: Boolean,
      default: true,
    },
    bubbleColor: {
      type: String,
      default: '#2e9b4a',
    },
    duration: {
      type: String,
      default: '0',
    },
    fileSize: {
      type: String,
      default: '0',
    },
    fileName: {
      type: String,
      default: '',
    },
    messageId: {
      type: String,
      default: '',
    },
    chatId: {
      type: String,
      default: '',
    },
  },
  setup(props) {
    const cStore = useChatStore()
    const showImage = ref<boolean>(false)
    const zoomRatio = 1.5
    const legacyBubbleColor = computed(() => `${props.bubbleColor}19`)

    // Grade messages are backend-provided JSON. Normalize the five optional labels so a malformed
    // system message cannot interrupt rendering of the rest of the conversation.
    const ratingContent = computed<RatingContent>(() => {
      if (props.type !== MessageType.GC) return { ...EMPTY_RATING_CONTENT }

      try {
        const parsedContent: unknown = JSON.parse(props.text)
        if (
          typeof parsedContent !== 'object' ||
          parsedContent === null ||
          Array.isArray(parsedContent)
        ) {
          return { ...EMPTY_RATING_CONTENT }
        }

        return RATING_SCORES.reduce<RatingContent>(
          (normalizedContent, score) => {
            const label = Reflect.get(parsedContent, score)
            normalizedContent[score] = typeof label === 'string' ? label : ''
            return normalizedContent
          },
          { ...EMPTY_RATING_CONTENT },
        )
      } catch {
        return { ...EMPTY_RATING_CONTENT }
      }
    })

    const isFileMessage = computed(
      () => props.fileSize !== undefined && props.fileSize !== null && props.fileSize !== '0',
    )
    const isPlainTextMessage = computed(
      () =>
        !isFileMessage.value &&
        !props.type.includes('image') &&
        !props.type.includes('video') &&
        !props.type.includes('email') &&
        props.type !== MessageType.RECOM &&
        props.type !== MessageType.MQ,
    )

    // matchQuestions 气泡：把 content 里的 JSON 数组解析出来供 MatchQuestionsCard 使用；
    // queId 是 19 位雪花 ID（> 2^53），必须走 parseMatchQuestions 的字符串化保护，
    // 否则 JSON.parse 会精度截断，导致后续接口打点对不上后端记录。
    const matchQuestionItems = computed<MatchQuestionItem[]>(() => {
      if (props.type !== MessageType.MQ) return []
      return parseMatchQuestions(props.text)
    })

    const openMatchQuestions = inject<OpenMatchQuestionsFn | undefined>('openMatchQuestions', undefined)
    const handleOpenMatchQuestions = () => {
      // 历史会话（chatId 与当前活跃会话不同）里的 matchQuestions 徽标不再打开弹窗——
      // 弹窗内的追问/打点接口都以当前 chatId 为上下文，用旧会话的数据发起会污染统计并可能触发后端 400。
      if (props.chatId && props.chatId !== cStore.chatId) return
      openMatchQuestions?.({
        items: sortMatchQuestions(matchQuestionItems.value),
        messageId: props.messageId,
      })
    }

    // 从 "YYYY-MM-DD HH:mm:ss" 切出 "HH:mm" 供气泡右下角内嵌时间使用；
    // 直接字符串切片，避免 new Date() 对非标准格式的时区解析歧义（与 TimeStamp 同源策略）。
    const shortTime = computed(() => {
      const parts = props.timestamp.split(' ')
      if (parts.length < 2) return ''
      return parts[1].slice(0, 5)
    })

    // 气泡内嵌时间的颜色 class：访客本人使用半透明白色，客服与机器人使用弱文字色。
    const timeColorClass = computed(() => (props.isMe ? 'time-me' : 'time-assist'))

    const getAvatar = (id: string) => resolveAvatar(id)

    return {
      cStore,
      isPlainTextMessage,
      legacyBubbleColor,
      matchQuestionItems,
      handleOpenMatchQuestions,
      ratingContent,
      showImage,
      zoomRatio,
      getAvatar,
      shortTime,
      timeColorClass,
      SenderType,
      MessageType,
    }
  },
})
</script>

<style lang="less" scoped>
.chat-content {
  display: flex;
  flex-direction: column;
}

.system-message {
  display: flex;
  align-items: center;
  width: 100%;
  justify-content: center;
  margin-top: 24px;

  &>span {
    background-color: #f4f6f5;
    padding: 2px 6px;
    border-radius: 4px;
    text-align: center;
    color: #616961;
  }
}

.tip-wrapper {
  display: flex;
  align-items: center;
  min-width: 0;
  max-width: 100%;
}

// matchQuestions 气泡外部的 "+N" 徽标：与气泡在 tip-wrapper 内并排显示
.mq-more-outside {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: 8px;
  width: 20px;
  height: 20px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: #edefed;
  color: #9c9f9d;
  font-family: inherit;
  font-size: 12px;
  font-weight: 500;
  line-height: 12px;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    filter: brightness(0.95);
  }

  &:focus-visible {
    outline: 2px solid var(--abot-brand-color, #2e9b4a);
    outline-offset: 2px;
  }
}

.main-chat {
  display: grid;
  grid-template-columns: 36px minmax(0, 1fr);
  column-gap: 8px;
  margin-top: 8px;

  &-wrapper {
    grid-column: 2;
    display: flex;
    flex-direction: column;
    align-items: start;
    min-width: 0;
    max-width: 100%;
  }

  &-bubble {
    padding: 8px 12px;
    background-color: #e0e3e0;
    border-radius: 4px;
    width: fit-content;
    max-width: 100%;
    height: fit-content;
    box-sizing: border-box;

    &-text {
      white-space: pre-line;
      word-break: break-word;
      overflow-wrap: break-word;
      display: inline-block;
      max-width: 100%;
      color: #1d2920;
    }

    // 气泡内嵌时间：inline-block 跟随上方文本末尾——短文本时贴右同行，
    // 长文本撑满后自然落到下一行末尾，形成“右下角”视觉效果。
    // 颜色由 timeColorClass 决定（time-me / time-assist）。
    &-time {
      display: inline-block;
      font-size: 10px;
      line-height: 14px;
      margin-left: 8px;
      vertical-align: bottom;
      white-space: nowrap;

      &.time-me {
        color: rgba(255, 255, 255, 0.8);
      }

      &.time-assist {
        color: var(--text-3);
      }
    }

    &.plain-bubble {
      border-radius: 8px;
      background-color: #fff;

      .main-chat-bubble-text {
        color: var(--text-primary);
      }

      &.my-chat {
        background-color: var(--bubble-color);

        .main-chat-bubble-text {
          color: #fff;
        }
      }
    }

    &.is-agent:not(.plain-bubble) {
      background-color: white;
    }

    &.is-recom {
      background-color: #fff;
      border-radius: 8px;
    }

    &.is-mq {
      background-color: #fff;
      border-radius: 8px;
      // 不再强制 100% 宽度：气泡外侧需要为 mq-more-outside 徽标留出空间，
      // 让气泡按内容自适应，与徽标在 tip-wrapper 内并排排布。
      max-width: 100%;
      min-width: 0;
      flex: 0 1 auto;
    }

    &.my-chat:not(.plain-bubble) {
      background-color: var(--legacy-bubble-color);
      margin-right: 0px;
      margin-left: 0px;
    }

    &.first-chat {
      margin-inline: 0px;
    }
  }

  &.my-chat {
    display: flex;
    justify-content: flex-end;
    padding-left: 44px;

    .main-chat-wrapper {
      align-items: flex-end;
    }
  }

  &.first-chat {
    margin-top: 20px;
  }
}

.message-avatar {
  grid-column: 1;
  grid-row: 1;
}

.username-label {
  color: #616961;
}

.comment-wrapper {
  display: flex;
  width: 100%;
  margin-top: 20px;
  box-sizing: border-box;
}

.full-screen-image {
  position: fixed;
  width: 100%;
  height: 100%;
  background-color: #00000099;
  top: 0px;
  left: 0px;
  z-index: 9999;
  display: grid;
  place-items: center;
}

.plain-text {
  display: flex;
  align-items: end;
}

.v-leave-to,
.v-enter-active {
  transition: all 0.3s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}
</style>
